import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { customersQuery, type CustomerFilters } from '../../../lib/queries'
import { kg, orderDate } from '../../../lib/format'
import { FilterView, Toolbar, ToolButton, tdCls, thCls } from '../../../components/filters'
import { CustomerForm } from '../../../components/customer-form'
import { Avatar, Button, PageHeader, Cylinder, Empty, ErrorBox, Icon, Menu, MenuItem, Modal, Pagination, Skeleton, Tag, cx, levelWord, useMedia } from '../../../components/ui'

type Search = CustomerFilters & { view?: 'cards' | 'table' }

export const Route = createFileRoute('/_app/customers/')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    state: (s.state as string) || undefined, region: (s.region as string) || undefined, outletId: (s.outletId as string) || undefined,
    q: (s.q as string) || undefined, page: Number(s.page) > 1 ? Number(s.page) : undefined,
    level: ['full', 'mid', 'low'].includes(s.level as string) ? (s.level as Search['level']) : undefined,
    sort: ['name', 'level', 'lastOrder'].includes(s.sort as string) ? (s.sort as Search['sort']) : undefined,
    view: s.view === 'cards' || s.view === 'table' ? s.view : undefined,
    status: s.status === 'pending' ? 'pending' : undefined,
  }),
  loaderDeps: ({ search: { view: _, ...f } }) => f,
  loader: ({ context, deps }) => context.queryClient.prefetchQuery(customersQuery(deps)),
  component: Customers,
})

const SORTS = [['recent', 'Newest'], ['name', 'Name A–Z'], ['level', 'Lowest gas first'], ['lastOrder', 'Last order']] as const
const LEVELS = [[undefined, 'All levels'], ['low', 'Low'], ['mid', 'Mid'], ['full', 'Full']] as const

function Customers() {
  const { view, ...filters } = Route.useSearch()
  const navigate = Route.useNavigate()
  const set = (patch: Partial<Search>) => navigate({ search: (s) => ({ ...s, page: undefined, ...patch }), replace: true })
  const { data, error, refetch, isPlaceholderData } = useQuery(customersQuery(filters))
  const [adding, setAdding] = useState(false)
  const [menu, setMenu] = useState<'filter' | 'sort' | null>(null)
  // Phones default to cards; the table needs width.
  const narrow = useMedia('(max-width: 639px)')
  const cards = view ? view === 'cards' : narrow

  return (
    <div>
      <PageHeader title="Customers" subtitle="Everyone you supply, their cylinders and gas levels."
        actions={<Button onClick={() => setAdding(true)}><Icon name="add" className="size-4" />Add customer</Button>} />
      <FilterView value={filters} onChange={(p) => set({ ...p, state: p.state, region: p.region, outletId: p.outletId })} section="Customers" icon="address-book" />
      <div className="mt-10 mb-3 flex items-end justify-between">
        <h2 className="text-sm font-semibold">Customers List</h2>
        <button onClick={() => set({ view: cards ? 'table' : 'cards', page: filters.page })} className="flex items-center gap-2 text-xs hover:text-brand">
          <Icon name="grid-view" className="size-5" />Switch to {cards ? 'table' : 'cards'} view
        </button>
      </div>
      <Toolbar search={filters.q ?? ''} onSearch={(q) => set({ q: q || undefined })}>
        <div className="relative">
          <ToolButton icon="funnel" label="Filter by gas level" active={!!filters.level} onClick={() => setMenu(menu === 'filter' ? null : 'filter')} />
          {menu === 'filter' && (
            <Menu onClose={() => setMenu(null)}>
              {LEVELS.map(([v, l]) => <MenuItem key={l} active={filters.level === v} onClick={() => (set({ level: v }), setMenu(null))}>{l}</MenuItem>)}
            </Menu>
          )}
        </div>
        <div className="relative">
          <ToolButton icon="sort" label="Sort" active={!!filters.sort} onClick={() => setMenu(menu === 'sort' ? null : 'sort')} />
          {menu === 'sort' && (
            <Menu onClose={() => setMenu(null)}>
              {SORTS.map(([v, l]) => <MenuItem key={v} active={(filters.sort ?? 'recent') === v} onClick={() => (set({ sort: v === 'recent' ? undefined : v }), setMenu(null))}>{l}</MenuItem>)}
            </Menu>
          )}
        </div>
        {!!data?.pending && (
          <button onClick={() => set({ status: filters.status ? undefined : 'pending' })} className={cx('rounded-full px-2.5 py-1 text-xs font-semibold', filters.status ? 'bg-brand text-white' : 'bg-peach text-ink')}>
            {data.pending} awaiting approval
          </button>
        )}
        {filters.level && <span className="rounded-full bg-white px-2 text-xs capitalize">{filters.level} gas <button onClick={() => set({ level: undefined })} aria-label="Clear gas filter">×</button></span>}
      </Toolbar>

      {error ? <div className="mt-4"><ErrorBox error={error} onRetry={refetch} /></div> : !data ? <Skeleton className="mt-2 h-96" /> : (
        <div className={cx('transition-opacity', isPlaceholderData && 'opacity-60')}>
          {data.customers.length === 0 ? (
            <Empty title="No customers match">Try another filter, or add a customer with the + button.</Empty>
          ) : cards ? (
            <ul className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 min-[1900px]:grid-cols-5">
              {data.customers.map((c) => (
                <li key={c.id}>
                  <Link to="/customers/$customerId" params={{ customerId: c.id }} className="block rounded-card bg-white p-4 hover:shadow-card">
                    <div className="flex items-center gap-3">
                      <Avatar name={c.name} photoKey={c.photoKey} className="size-10 text-sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{c.name} {c.isRetailAgent && <Tag>Retail Agent</Tag>}{c.status === 'pending' && <PendingTag />}</p>
                        <p className="truncate text-xs text-muted">{c.address}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-end justify-between text-xs">
                      <span className="flex items-center gap-2"><Cylinder level={c.level} />{levelWord(c.level)}, {c.levelKg}/{kg(c.cylinderKg)}</span>
                      <span className="text-muted">{orderDate(c.lastOrderAt)}</span>
                    </div>
                    <div className="mt-2 h-1 overflow-hidden rounded-full bg-black/5"><div className={cx('h-full rounded-full', c.level === 'full' ? 'bg-good-2' : c.level === 'mid' ? 'bg-[#e8a33d]' : 'bg-brand')} style={{ width: `${Math.min(100, (c.levelKg / c.cylinderKg) * 100)}%` }} /></div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-2 overflow-x-auto bg-white">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr>
                    <th className={thCls}>User</th>
                    <th className={thCls}>Estimated Tank Storage</th>
                    <th className={thCls}>Address</th>
                    <th className={thCls}>Last Order</th>
                    <th className={thCls}>Gas level</th>
                  </tr>
                </thead>
                <tbody>
                  {data.customers.map((c) => (
                    <tr key={c.id} className="group border-b border-black/5 last:border-0 hover:bg-page/70">
                      <td className={tdCls}>
                        <Link to="/customers/$customerId" params={{ customerId: c.id }} className="flex items-center gap-2 hover:text-brand">
                          <Avatar name={c.name} photoKey={c.photoKey} />{c.name}{c.isRetailAgent && <Tag>Retail Agent</Tag>}{c.status === 'pending' && <PendingTag />}
                        </Link>
                      </td>
                      <td className={tdCls}>{kg(c.cylinderKg)}</td>
                      <td className={cx(tdCls, 'max-w-[280px] truncate')}>{c.address}</td>
                      <td className={tdCls}><span className="flex items-center gap-1.5"><Icon name="calendar" className="size-4" />{orderDate(c.lastOrderAt)}</span></td>
                      <td className={tdCls}><span className="flex items-center gap-2"><Cylinder level={c.level} /><span className="num">{levelWord(c.level)}, {c.levelKg}/{kg(c.cylinderKg)}</span></span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={data.page} total={data.total} pageSize={data.pageSize} onPage={(page) => navigate({ search: (s) => ({ ...s, page: page > 1 ? page : undefined }) })} />
        </div>
      )}

      <Modal open={adding} onClose={() => setAdding(false)} title="Add customer" wide>
        <CustomerForm defaultOutletId={filters.outletId} onDone={(id) => (setAdding(false), navigate({ to: '/customers/$customerId', params: { customerId: id } }))} />
      </Modal>
    </div>
  )
}

const PendingTag = () => <span className="ml-1 inline-block rounded-md bg-ink px-1.5 text-[8px] leading-4 font-semibold whitespace-nowrap text-white">Awaiting approval</span>
